// Same-folder imports with `.ts`: the Node test runner can't resolve `@/`.

export interface GoogleCallbackParams {
  idToken: string | null;
  state: string | null;
  error: string | null;
}

/**
 * Google returns the ID token in the callback URL's fragment
 * (`#id_token=…&state=…`), or an `error` such as `access_denied` when the
 * user cancels.
 */
export const readGoogleCallbackParams = (
  callbackUrl: string,
): GoogleCallbackParams => {
  const fragment = new URL(callbackUrl).hash.replace(/^#/, '');
  const params = new URLSearchParams(fragment);

  return {
    idToken: params.get('id_token'),
    state: params.get('state'),
    error: params.get('error'),
  };
};

/**
 * The `nonce` claim of an ID token, read without verifying the signature:
 * the API verifies the token, this only ties it to the sign-in this browser
 * started. Undefined when the token can't be read.
 */
export const readIdTokenNonce = (idToken: string): string | undefined => {
  const payload = idToken.split('.')[1];
  if (!payload) {
    return undefined;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));

    const nonce =
      typeof claims === 'object' && claims !== null && 'nonce' in claims
        ? claims.nonce
        : undefined;
    return typeof nonce === 'string' ? nonce : undefined;
  } catch {
    return undefined;
  }
};
